import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioModule } from '@angular/material/radio';

export interface LinkDialogData {
  url: string;
  displayText: string;
  isRemoving?: boolean;
  isEditing?: boolean;
  showDeleteOption?: boolean;
  selectedText?: string;
}

@Component({
  selector: 'app-link-dialog',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatRadioModule
  ],
  template: `
    <h2 mat-dialog-title>
      <ng-container *ngIf="data.isRemoving">Remove Link</ng-container>
      <ng-container *ngIf="data.isEditing && !data.isRemoving">Edit Link</ng-container>
      <ng-container *ngIf="!data.isEditing && !data.isRemoving">Insert Link</ng-container>
    </h2>
    <div class="dialog-content">
      <ng-container *ngIf="!data.isRemoving">
        <form [formGroup]="linkForm">
          <mat-form-field appearance="outline">
            <mat-label>Display Text</mat-label>
            <input matInput formControlName="displayText" placeholder="Link text to display">
            <mat-error *ngIf="linkForm.get('displayText')?.hasError('required')">Display text is required</mat-error>
          </mat-form-field>
          
          <mat-form-field appearance="outline">
            <mat-label>URL</mat-label>
            <input matInput formControlName="url" placeholder="https://example.com">
            <mat-error *ngIf="linkForm.get('url')?.hasError('required')">URL is required</mat-error>
            <mat-error *ngIf="linkForm.get('url')?.hasError('pattern')">Please enter a valid URL</mat-error>
            <mat-hint>Include https:// for external links</mat-hint>
          </mat-form-field>
          
          <div *ngIf="isTestingUrl" class="url-testing">
            <mat-spinner diameter="24"></mat-spinner>
            <span>Testing URL...</span>
          </div>
          
          <div *ngIf="urlTestResult && !isTestingUrl" 
               [ngClass]="{'url-success': urlTestResult.success, 'url-error': !urlTestResult.success}"
               class="url-test-result">
            <span *ngIf="urlTestResult.success">
              <mat-icon>check_circle</mat-icon> URL is valid
            </span>
            <span *ngIf="!urlTestResult.success">
              <mat-icon>error</mat-icon> {{ urlTestResult.message }}
            </span>
          </div>
          
          <!-- Delete options only visible when editing existing links -->
          <div *ngIf="data.showDeleteOption" class="delete-options">
            <h3 class="delete-header">Remove Options</h3>
            <div class="checkbox-group">
              <mat-checkbox [(ngModel)]="removeLinkOnly" 
                           [ngModelOptions]="{standalone: true}"
                           (change)="onRemoveLinkOnlyChange()">
                <mat-icon>link_off</mat-icon>
                Remove link only (keep text)
              </mat-checkbox>
              <mat-checkbox [(ngModel)]="removeLinkAndText" 
                           [ngModelOptions]="{standalone: true}"
                           (change)="onRemoveLinkAndTextChange()">
                <mat-icon>delete</mat-icon>
                Remove link and text
              </mat-checkbox>
            </div>
            <p class="helper-text" *ngIf="removeLinkOnly || removeLinkAndText">
              <mat-icon>info</mat-icon>
              {{ getRemoveHelperText() }}
            </p>
          </div>
          
          <div class="dialog-actions-row">
            <button mat-button (click)="testUrl()" 
                    [disabled]="linkForm.get('url')?.invalid || isTestingUrl">
              <mat-icon>public</mat-icon> Test URL
            </button>
          </div>
        </form>
      </ng-container>
      
      <ng-container *ngIf="data.isRemoving">
        <p>What would you like to do with the link on "{{ data.selectedText }}"?</p>
        <div class="checkbox-group-standalone">
          <mat-checkbox [(ngModel)]="removeLinkOnly" 
                       (change)="onRemoveLinkOnlyChange()">
            <mat-icon>link_off</mat-icon>
            Remove link only (keep text)
          </mat-checkbox>
          <mat-checkbox [(ngModel)]="removeLinkAndText" 
                       (change)="onRemoveLinkAndTextChange()">
            <mat-icon>delete</mat-icon>
            Remove link and text
          </mat-checkbox>
        </div>
        <p class="helper-text" *ngIf="removeLinkOnly || removeLinkAndText">
          <mat-icon>info</mat-icon>
          {{ getRemoveHelperText() }}
        </p>
      </ng-container>
    </div>
    <div class="dialog-actions">
      <button mat-button mat-dialog-close>Cancel</button>
      <button *ngIf="!data.isRemoving" 
              mat-raised-button 
              color="primary" 
              [disabled]="shouldDisableUpdateButton()" 
              (click)="onConfirm()">
              {{ getActionButtonText() }}
      </button>
      <button *ngIf="data.isRemoving" 
              mat-raised-button 
              color="warn"
              [disabled]="!removeLinkOnly && !removeLinkAndText"
              (click)="onRemoveConfirm()">Remove</button>
    </div>
  `,
  styles: [`
    mat-form-field {
      width: 100%;
      min-width: 300px;
      margin-bottom: 16px;
    }
    
    .dialog-content {
      padding: 16px 24px;
      margin: 0;
      min-width: 400px;
    }
    
    .dialog-actions {
      display: flex;
      justify-content: flex-end;
      padding: 8px 24px 16px;
      gap: 8px;
    }
    
    .dialog-actions-row {
      display: flex;
      justify-content: flex-end;
      margin-bottom: 16px;
    }
    
    .url-test-result {
      padding: 8px 12px;
      border-radius: 4px;
      margin-bottom: 16px;
      display: flex;
      align-items: center;
    }
    
    .url-success {
      background-color: rgba(76, 175, 80, 0.1);
      color: #4caf50;
    }
    
    .url-error {
      background-color: rgba(244, 67, 54, 0.1);
      color: #f44336;
    }
    
    .url-testing {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 16px;
      color: #666;
    }
    
    .delete-options {
      margin: 24px 0 16px;
      padding: 16px;
      border-radius: 4px;
      background-color: rgba(244, 67, 54, 0.05);
      border: 1px solid rgba(244, 67, 54, 0.2);
    }
    
    .delete-header {
      margin: 0 0 12px 0;
      font-size: 14px;
      font-weight: 500;
      color: #f44336;
    }
    
    .checkbox-group, .checkbox-group-standalone {
      display: flex;
      flex-direction: column;
      gap: 12px;
    }
    
    .checkbox-group-standalone {
      margin: 16px 0;
    }
    
    .helper-text {
      margin: 12px 0 0;
      padding: 8px;
      background-color: rgba(0, 0, 0, 0.03);
      border-radius: 4px;
      font-size: 13px;
      color: #666;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .helper-text mat-icon {
      font-size: 18px;
      width: 22px;
      height: 18px;
      color: #1976d2;
    }
    
    mat-icon {
      margin-right: 4px;
      vertical-align: middle;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }
    
    mat-checkbox {
      display: flex;
      align-items: center;
    }
    
    mat-checkbox mat-icon {
      margin-left: 8px;
      margin-right: 8px;
    }

    // Dark Theme Support
    :host-context(body.dark-theme) {
      h2[mat-dialog-title] {
        color: var(--form-text-primary, #ffffff);
      }

      .dialog-content {
        color: var(--form-text-primary, #ffffff);

        ::ng-deep {
          .mat-mdc-form-field {
            .mdc-text-field {
              background-color: var(--form-input-bg, #2d3748);
            }

            .mdc-notched-outline__leading,
            .mdc-notched-outline__notch,
            .mdc-notched-outline__trailing {
              border-color: var(--form-input-border, #4a5568);
            }

            &.mat-focused {
              .mdc-notched-outline__leading,
              .mdc-notched-outline__notch,
              .mdc-notched-outline__trailing {
                border-color: var(--form-primary-color, #00a1ff);
              }
            }

            .mat-mdc-form-field-label,
            .mdc-floating-label,
            input {
              color: var(--form-text-primary, #ffffff);
            }

            mat-hint {
              color: var(--form-text-secondary, #b0b0b0);
            }
          }
        }

        p {
          color: var(--form-text-primary, #ffffff);
        }
      }

      .url-test-result {
        &.url-success {
          background-color: var(--status-low-bg, rgba(102, 187, 106, 0.15));
          color: var(--status-low-text, #66bb6a);

          mat-icon {
            color: var(--status-low-text, #66bb6a);
          }
        }

        &.url-error {
          background-color: var(--status-high-bg, rgba(239, 83, 80, 0.15));
          color: var(--status-high-text, #ef5350);

          mat-icon {
            color: var(--status-high-text, #ef5350);
          }
        }
      }

      .url-testing {
        color: var(--form-text-secondary, #b0b0b0);

        ::ng-deep {
          .mat-mdc-progress-spinner circle {
            stroke: var(--form-primary-color, #00a1ff);
          }
        }
      }

      .delete-options {
        background-color: var(--status-high-subtle, rgba(239, 83, 80, 0.15));
        border-color: var(--status-high-text, #ef5350);

        .delete-header {
          color: var(--status-high-text, #ef5350);
        }
      }

      .checkbox-group, .checkbox-group-standalone {
        ::ng-deep {
          .mat-mdc-checkbox {
            .mdc-checkbox__background {
              border-color: var(--form-border-color, #4a5568);
            }

            .mdc-label {
              color: var(--form-text-primary, #ffffff);
            }
          }

          mat-icon {
            color: var(--form-text-secondary, #b0b0b0);
          }
        }
      }

      .helper-text {
        background-color: rgba(255, 255, 255, 0.05);
        color: var(--form-text-secondary, #b0b0b0);

        mat-icon {
          color: var(--form-primary-color, #00a1ff);
        }
      }

      .dialog-actions {
        button {
          &.mat-mdc-button {
            color: var(--form-text-secondary, #b0b0b0);

            &:hover {
              background-color: var(--form-cancel-hover-bg, #3a3a3a);
            }
          }

          &.mat-mdc-raised-button {
            &[color="primary"] {
              background-color: var(--form-primary-color, #00a1ff);
              color: var(--form-text-on-primary, #ffffff);

              &:hover {
                background-color: var(--form-primary-hover, #0086d9);
              }

              &[disabled] {
                background-color: var(--form-button-disabled-bg, #3a3a3a);
                color: var(--form-button-disabled-text, #808080);
              }
            }

            &[color="warn"] {
              background-color: var(--form-error-color, #ef5350);
              color: var(--form-text-on-primary, #ffffff);

              &:hover {
                background-color: #d32f2f;
              }
            }
          }
        }
      }

      .dialog-actions-row {
        button {
          color: var(--form-text-secondary, #b0b0b0);

          &:hover:not([disabled]) {
            background-color: var(--form-cancel-hover-bg, #3a3a3a);
          }

          mat-icon {
            color: var(--form-text-secondary, #b0b0b0);
          }
        }
      }
    }
  `]
})
export class LinkDialogComponent {
  // URL validation pattern
  urlPattern = '^(https?:\\/\\/)?'+ 
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|'+ 
    '((\\d{1,3}\\.){3}\\d{1,3}))'+ 
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ 
    '(\\?[;&a-z\\d%_.~+=-]*)?'+ 
    '(\\#[-a-z\\d_]*)?$';
  
  // Remove options
  removeLinkOnly = false;
  removeLinkAndText = false;
  
  // URL testing state
  isTestingUrl = false;
  urlTestResult: { success: boolean, message?: string } | null = null;
  
  // Form group
  linkForm = new FormGroup({
    displayText: new FormControl('', [Validators.required]),
    url: new FormControl('', [
      Validators.required,
      Validators.pattern(this.urlPattern)
    ])
  });

  constructor(
    public dialogRef: MatDialogRef<LinkDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: LinkDialogData
  ) {
    // Populate fields for editing
    if (data && !data.isRemoving) {
      if (data.url) {
        this.linkForm.get('url')?.setValue(data.url);
      }
      
      if (data.displayText) {
        this.linkForm.get('displayText')?.setValue(data.displayText);
      }
    }
    
    // Reset test result on URL change
    this.linkForm.get('url')?.valueChanges.subscribe(() => {
      this.urlTestResult = null;
    });
  }

  // Handle checkbox changes to ensure only one is selected
  onRemoveLinkOnlyChange(): void {
    if (this.removeLinkOnly) {
      this.removeLinkAndText = false;
    }
  }

  onRemoveLinkAndTextChange(): void {
    if (this.removeLinkAndText) {
      this.removeLinkOnly = false;
    }
  }

  getRemoveHelperText(): string {
    if (this.removeLinkAndText) {
      return 'This will remove both the link and the text from the document.';
    }
    if (this.removeLinkOnly) {
      return 'This will remove the link but keep the text as plain text.';
    }
    return '';
  }

  getActionButtonText(): string {
    if (this.removeLinkOnly || this.removeLinkAndText) {
      return 'Update & Remove';
    }
    return this.data.isEditing ? 'Update' : 'Insert';
  }

  shouldDisableUpdateButton(): boolean {
    // If remove options are selected, we don't need valid form
    if (this.removeLinkOnly || this.removeLinkAndText) {
      return false;
    }
    // Otherwise, form must be valid
    return this.linkForm.invalid;
  }

  testUrl(): void {
    const url = this.linkForm.get('url')?.value;
    if (!url) return;
    
    this.isTestingUrl = true;
    
    let testUrl = url;
    if (!testUrl.startsWith('http://') && !testUrl.startsWith('https://')) {
      testUrl = 'https://' + testUrl;
    }
    
    fetch(testUrl, { 
      method: 'HEAD',
      mode: 'no-cors'
    })
    .then(() => {
      this.urlTestResult = { success: true };
    })
    .catch(error => {
      this.urlTestResult = { 
        success: false, 
        message: 'URL might not be accessible'
      };
    })
    .finally(() => {
      this.isTestingUrl = false;
    });
  }

  onConfirm(): void {
    // Handle remove actions
    if (this.removeLinkOnly || this.removeLinkAndText) {
      this.dialogRef.close({
        removeLink: true,
        removeText: this.removeLinkAndText // true if removing text, false if keeping text
      });
      return;
    }
    
    // Handle normal update/insert
    if (this.linkForm.valid) {
      let url = this.linkForm.get('url')?.value || '';
      if (url && !url.startsWith('http://') && !url.startsWith('https://')) {
        url = 'https://' + url;
      }
      
      this.dialogRef.close({
        url: url,
        displayText: this.linkForm.get('displayText')?.value
      });
    }
  }
  
  onRemoveConfirm(): void {
    this.dialogRef.close({
      removeLink: true,
      removeText: this.removeLinkAndText
    });
  }
}